import type { AxiosError } from 'axios';

import { Schemas } from '@api-client/generated/types';
import { countries } from '@constants';

import { ACCOUNTING_PLANS, PersonType } from './types';

/**
 * Whether a failed incorporation request means the company simply has no onboarding data.
 * `GET /admin/incorporations` 404s both when the incorporation row itself is missing and when
 * a person it refers to is gone — either way the company never completed KYB, which is a
 * permanent state worth reporting rather than a failure worth retrying.
 */
export const isMissingIncorporation = (error: unknown): boolean =>
  (error as AxiosError)?.response?.status === 404;

/** react-query's default retry count, kept for anything that might actually be transient. */
const MAX_INCORPORATION_RETRIES = 3;

/**
 * react-query `retry` value for an incorporation query: retry real failures as usual, but
 * answer a missing incorporation straight away — retrying it only delays the message.
 */
export const shouldRetryIncorporation = (
  failureCount: number,
  error: unknown
): boolean =>
  !isMissingIncorporation(error) && failureCount < MAX_INCORPORATION_RETRIES;

export const geyKycStatus = ({
  isKycFailed,
  isCheckComplete,
  isSubmittedToKyc,
}: {
  isKycFailed: boolean;
  isCheckComplete: boolean;
  isSubmittedToKyc: boolean;
}) => {
  if (isKycFailed)
    return {
      color: 'error',
      label: 'Failed',
    };

  if (isCheckComplete)
    return {
      color: 'success',
      label: 'Passed',
    };

  if (isSubmittedToKyc)
    return {
      color: 'processing',
      label: 'Submitted',
    };

  return {
    color: 'processing',
    label: 'Pending',
  };
};

export const getAccountingPlan = (incorporation: Schemas.Incorporation) =>
  'accountingPlanId' in incorporation && incorporation.accountingPlanId
    ? ACCOUNTING_PLANS[
        incorporation.accountingPlanId as keyof typeof ACCOUNTING_PLANS
      ]
    : '-';

export const getPersonByType = (type: PersonType) => {
  if (type === PersonType.Physical) return 'Person';

  if (type === PersonType.Legal) return 'Company';
};

export const getDetailsByCompanyRelations = (
  data: Schemas.CompanyRelationsDto
) =>
  Object.entries({
    Shareholder: data.isShareholder,
    Director: data.isDirector,
    UBO: data.isUbo,
  })
    .reduce<string[]>((acc, [role, isActive]) => {
      if (isActive) acc.push(role);
      return acc;
    }, [])
    .join(', ');

export const getCountryDataByCode = (code: string | string[]) =>
  (Array.isArray(code) ? code : [code])
    .map((val) => countries.find((pair) => pair.code === val)?.name || val)
    .join(', ');
